// LessonPlayer — walks a lesson's moments one at a time with progress dots
// and a Continue button. Each moment decides its own readiness via
// onReadyChange (explain: always; listen: after a listen + a pick; tinker:
// always; echo: after every call is visited or skipped; try: after saving a
// keeper). Keepers are collected keyed by keeperId — matching the progress
// state's `keepers` shape — as { [keeperId]: { keeperName, phrase, lessonId,
// savedAt } }; the final Continue calls onComplete(keepers).
//
// Moments are keyed by id, so advancing unmounts the old one — every moment
// stops its own drones/voices/playback in its unmount cleanup, which is what
// keeps audio silent across moment changes and on leaving the lesson.
//
// Props: { lessonId, moments, onComplete(keepers) }.

import React, { useCallback, useRef, useState } from "react";
import ExplainMoment from "./moments/ExplainMoment";
import ListenMoment from "./moments/ListenMoment";
import TinkerGravity from "./moments/TinkerGravity";
import TinkerTour from "./moments/TinkerTour";
import TinkerEndingDrag from "./moments/TinkerEndingDrag";
import TinkerIntervalTour from "./moments/TinkerIntervalTour";
import TinkerChordTour from "./moments/TinkerChordTour";
import TinkerChordTones from "./moments/TinkerChordTones";
import TinkerGrid from "./moments/TinkerGrid";
import EchoMoment from "./moments/EchoMoment";
import TryMoment from "./moments/TryMoment";
import BuildMoment from "./moments/BuildMoment";
import DrumBuild from "./moments/DrumBuild";
import LayerBoard from "./moments/LayerBoard";
import StylePick from "./moments/StylePick";
import ArrangeBuild from "./moments/ArrangeBuild";

const TINKERS = {
  gravity: TinkerGravity,
  tour: TinkerTour,
  endingDrag: TinkerEndingDrag,
  intervalTour: TinkerIntervalTour,
  chordTour: TinkerChordTour,
  chordTones: TinkerChordTones,
  grid: TinkerGrid,
};

const BUILDS = {
  progression: BuildMoment,
  drums: DrumBuild,
  layers: LayerBoard,
  style: StylePick,
  arrange: ArrangeBuild,
};

const componentFor = (moment) => {
  switch (moment.type) {
    case "explain":
      return ExplainMoment;
    case "listen":
      return ListenMoment;
    case "tinker":
      return TINKERS[moment.variant] || null;
    case "echo":
      return EchoMoment;
    case "try":
      return TryMoment;
    case "build":
      return BUILDS[moment.variant] || BuildMoment;
    default:
      return null;
  }
};

export default function LessonPlayer({
  lessonId,
  moments,
  onComplete,
  onExit,
  keepers: savedKeepers,
}) {
  const [index, setIndex] = useState(0);
  const [ready, setReady] = useState(false);
  const keepersRef = useRef({});

  const handleReadyChange = useCallback((value) => setReady(Boolean(value)), []);
  // Third arg is either a bare phrase ({ notes }) from try/echo moments, or a
  // payload like { phrase, degrees } from build moments — spread payloads so
  // extras (degrees) sit beside `phrase` on the stored keeper.
  const handleKeeper = useCallback(
    (keeperId, keeperName, payload) => {
      const extra =
        payload && payload.notes ? { phrase: payload } : payload || {};
      keepersRef.current = {
        ...keepersRef.current,
        [keeperId]: { keeperName, ...extra, lessonId, savedAt: Date.now() },
      };
    },
    [lessonId]
  );

  if (!moments || !moments.length) return null;
  const moment = moments[index];
  const isLast = index === moments.length - 1;
  const Moment = componentFor(moment);

  // Keepers visible to moments (e.g. an explain demo that plays back the
  // keeper you just recorded): earlier runs' saves plus this run's.
  const keepers = { ...(savedKeepers || {}), ...keepersRef.current };

  const advance = () => {
    if (!ready) return;
    if (isLast) {
      onComplete(keepersRef.current);
      return;
    }
    setReady(false); // the next moment re-reports its own readiness on mount
    setIndex((i) => i + 1);
  };

  return (
    <div className="hk-lesson">
      <div
        className="hk-progress-dots"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={moments.length}
        aria-valuenow={index + 1}
        aria-label={`moment ${index + 1} of ${moments.length}`}
      >
        {moments.map((m, i) => (
          <span
            key={m.id}
            className={`hk-dot${
              i < index ? " hk-dot--done" : i === index ? " hk-dot--current" : ""
            }`}
            aria-hidden="true"
          />
        ))}
      </div>

      <div className="hk-moment" key={moment.id}>
        {Moment ? (
          <Moment
            moment={moment}
            keepers={keepers}
            onReadyChange={handleReadyChange}
            onKeeper={handleKeeper}
          />
        ) : (
          <UnknownMoment onReadyChange={handleReadyChange} />
        )}
      </div>

      <div className="hk-moment-footer">
        {onExit && (
          <button type="button" className="hk-btn hk-btn--ghost" onClick={onExit}>
            back to the map
          </button>
        )}
        <button
          type="button"
          className="hk-btn hk-btn--primary"
          disabled={!ready}
          onClick={advance}
        >
          {isLast ? "finish" : "continue"}
        </button>
      </div>
    </div>
  );
}

// A moment type this player doesn't know yet — don't strand the learner.
function UnknownMoment({ onReadyChange }) {
  React.useEffect(() => {
    onReadyChange(true);
  }, [onReadyChange]);
  return <div className="hk-moment-body">…</div>;
}
